import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  Copy,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import {
  prescriptionTemplateQueryKeys,
  prescriptionTemplatesApi,
} from "../../shared/api/prescriptionTemplates";
import type {
  PrescriptionTemplateListFilter,
  PrescriptionTemplateSummaryResponse,
} from "../../shared/types/prescriptionTemplate";
import { formatDate } from "../../shared/format";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PAGE_SIZE = 20;

export function PrescriptionTemplateListPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [icd10, setIcd10] = useState("");
  const [visibleOnly, setVisibleOnly] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const filter: PrescriptionTemplateListFilter = {
    q: debouncedQ || null,
    icd10Code: icd10.trim() || null,
    visibleOnly: visibleOnly ? true : null,
  };

  const list = useQuery({
    queryKey: prescriptionTemplateQueryKeys.list(filter, page),
    queryFn: () =>
      prescriptionTemplatesApi.list(filter, { page, size: PAGE_SIZE, sort: "name" }),
    placeholderData: (prev) => prev,
  });

  const remove = useMutation({
    mutationFn: (id: string) => prescriptionTemplatesApi.softDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: prescriptionTemplateQueryKeys.all }),
  });
  const toggleVisible = useMutation({
    mutationFn: ({ id, visible }: { id: string; visible: boolean }) =>
      visible ? prescriptionTemplatesApi.hide(id) : prescriptionTemplatesApi.show(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: prescriptionTemplateQueryKeys.all }),
  });
  const duplicate = useMutation({
    mutationFn: ({ id, code }: { id: string; code: string }) =>
      prescriptionTemplatesApi.duplicate(id, code),
    onSuccess: () => qc.invalidateQueries({ queryKey: prescriptionTemplateQueryKeys.all }),
  });

  const totalPages = list.data?.totalPages ?? 0;
  const totalElements = list.data?.totalElements ?? 0;

  return (
    <div>
      <PageHeader
        /* #19412: nói rõ màn này nằm dưới Đơn thuốc, và cho đường quay lại. */
        breadcrumbs={[{ label: "Đơn thuốc", to: "/prescriptions" }, { label: "Toa thuốc mẫu" }]}
        title="Toa thuốc mẫu"
        description="Lưu các tổ hợp chẩn đoán + danh sách thuốc thường dùng. Khi kê đơn, bác sĩ chọn một mẫu — toàn bộ thuốc sẽ tự điền vào đơn nháp để chỉnh sửa."
        actions={
          <Button asChild>
            <Link to="/prescriptions/templates/new">
              <Plus />
              Tạo toa mẫu
            </Link>
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Tìm theo mã, tên, chẩn đoán, ICD-10…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            className="pl-9"
          />
        </div>
        <Input
          type="text"
          placeholder="Mã ICD-10"
          value={icd10}
          onChange={(e) => {
            setIcd10(e.target.value.toUpperCase());
            setPage(0);
          }}
          maxLength={16}
          className="w-36"
        />
        <Label className="inline-flex items-center gap-2 text-sm font-normal">
          <Checkbox
            checked={visibleOnly}
            onCheckedChange={(checked) => {
              setVisibleOnly(checked === true);
              setPage(0);
            }}
          />
          Chỉ hiển thị
        </Label>
      </div>

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Mã toa mẫu</TableHead>
              <TableHead>Tên</TableHead>
              <TableHead>ICD-10</TableHead>
              <TableHead>Chẩn đoán</TableHead>
              <TableHead className="text-right">Số ngày</TableHead>
              <TableHead className="text-right">Số thuốc</TableHead>
              <TableHead>Hiển thị</TableHead>
              <TableHead>Cập nhật</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.isLoading && (
              <TableRow>
                <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                  Đang tải…
                </TableCell>
              </TableRow>
            )}
            {list.isError && (
              <TableRow>
                <TableCell colSpan={9} className="py-8 text-center text-destructive">
                  <span className="inline-flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4" />
                    {apiError(list.error).detail}
                  </span>
                </TableCell>
              </TableRow>
            )}
            {!list.isLoading && (list.data?.content.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                  Chưa có toa mẫu nào. Bấm "Tạo toa mẫu" để bắt đầu.
                </TableCell>
              </TableRow>
            )}
            {list.data?.content.map((t) => (
              <TemplateRow
                key={t.id}
                t={t}
                onToggleVisible={() =>
                  toggleVisible.mutate({ id: t.id, visible: t.visible })
                }
                onDelete={() => {
                  if (confirm(`Xoá toa mẫu "${t.name}"? Có thể khôi phục sau.`)) {
                    remove.mutate(t.id);
                  }
                }}
                onDuplicate={() => {
                  const code = prompt("Mã toa mẫu mới:", `${t.code}-COPY`);
                  if (code && code.trim()) duplicate.mutate({ id: t.id, code: code.trim() });
                }}
                busy={
                  remove.isPending || toggleVisible.isPending || duplicate.isPending
                }
              />
            ))}
          </TableBody>
        </Table>
        {totalPages > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
            <div className="text-sm text-muted-foreground">
              Hiển thị {page * PAGE_SIZE + 1}–
              {Math.min((page + 1) * PAGE_SIZE, totalElements)} trong {totalElements}
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={page === 0}
                onClick={() => setPage(0)}
                aria-label="Trang đầu"
              >
                <ChevronFirst />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
                aria-label="Trang trước"
              >
                <ChevronLeft />
              </Button>
              <span className="px-2 text-sm tabular-nums text-muted-foreground">
                {page + 1} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
                aria-label="Trang sau"
              >
                <ChevronRight />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(totalPages - 1)}
                aria-label="Trang cuối"
              >
                <ChevronLast />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TemplateRow({
  t,
  onToggleVisible,
  onDelete,
  onDuplicate,
  busy,
}: {
  t: PrescriptionTemplateSummaryResponse;
  onToggleVisible: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  busy: boolean;
}) {
  return (
    <TableRow>
      <TableCell className="font-mono">{t.code}</TableCell>
      <TableCell>
        <Link
          to={`/prescriptions/templates/${t.id}`}
          className="font-semibold text-primary hover:underline"
        >
          {t.name}
        </Link>
        {!t.active && (
          <Badge variant="muted" className="ml-1.5">
            Đã tắt
          </Badge>
        )}
      </TableCell>
      <TableCell className="font-mono">{t.icd10Code ?? "—"}</TableCell>
      <TableCell>{t.diagnosis ?? "—"}</TableCell>
      <TableCell className="text-right tabular-nums">{t.durationDays}</TableCell>
      <TableCell className="text-right tabular-nums">{t.itemCount}</TableCell>
      <TableCell>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onToggleVisible}
          disabled={busy}
        >
          {t.visible ? "✓ Hiển thị" : "Ẩn"}
        </Button>
      </TableCell>
      <TableCell className="font-mono">{formatDate(t.updatedAt)}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button asChild variant="outline" size="sm">
            <Link to={`/prescriptions/templates/${t.id}`}>Mở</Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onDuplicate}
            disabled={busy}
          >
            <Copy />
            Nhân bản
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={onDelete}
            disabled={busy}
          >
            <Trash2 />
            Xoá
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
